'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  Shield, Zap, Heart, Users,
  CheckCircle2, Lock, CreditCard, RefreshCw, Layers, Award,
  FileCheck, Shrink, Briefcase, Building2, GraduationCap
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { type Locale } from '@/lib/i18n/config';

interface AboutPageClientProps {
  locale: Locale;
}

export default function AboutPageClient({ locale }: AboutPageClientProps) {
  const t = useTranslations('aboutPage');

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(var(--color-background))]">
      <Header locale={locale} />

      <main className="flex-1 w-full flex flex-col items-center">
        <div className="w-full max-w-[70%] mx-auto">
          {/* Hero Section */}
          <section className="relative overflow-hidden py-20 lg:py-32 bg-[hsl(var(--color-background))]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[hsl(var(--color-primary)/0.1)] via-[hsl(var(--color-background))] to-transparent" />
            <div className="container relative mx-auto px-4 text-center z-10">
              <h1 className="text-4xl md:text-6xl font-black tracking-tight text-[hsl(var(--color-foreground))] mb-6">
                {t('hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-[hsl(var(--color-muted-foreground))] max-w-3xl mx-auto mb-10 font-medium">
                {t('hero.subtitle')}
              </p>
              <p className="text-lg text-[hsl(var(--color-muted-foreground))] max-w-2xl mx-auto mb-12 leading-relaxed">
                {t('hero.description')}
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-12">
                <div className="flex items-center gap-2 text-[hsl(var(--color-foreground))] font-medium">
                  <Shield className="w-5 h-5 text-emerald-500" />
                  <span>{t('hero.trust.privacy')}</span>
                </div>
                <div className="flex items-center gap-2 text-[hsl(var(--color-foreground))] font-medium">
                  <Lock className="w-5 h-5 text-blue-500" />
                  <span>{t('hero.trust.security')}</span>
                </div>
                <div className="flex items-center gap-2 text-[hsl(var(--color-foreground))] font-medium">
                  <CheckCircle2 className="w-5 h-5 text-purple-500" />
                  <span>{t('hero.trust.noInstall')}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Who We Are */}
          <section className="py-20 bg-[hsl(var(--color-muted)/0.3)] rounded-3xl my-8">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold mb-6 text-[hsl(var(--color-foreground))]">{t('whoWeAre.title')}</h2>
                <p className="text-lg text-[hsl(var(--color-muted-foreground))] leading-relaxed">
                  {t('whoWeAre.description')}
                </p>
              </div>
            </div>
          </section>

          {/* What We Do */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold mb-4 text-[hsl(var(--color-foreground))]">{t('whatWeDo.title')}</h2>
                <p className="text-[hsl(var(--color-muted-foreground))] max-w-2xl mx-auto">
                  {t('whatWeDo.description')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { key: 'convert', icon: RefreshCw, color: 'text-blue-500' },
                  { key: 'merge', icon: Layers, color: 'text-emerald-500' },
                  { key: 'split', icon: FileCheck, color: 'text-orange-500' },
                  { key: 'compress', icon: Shrink, color: 'text-red-500' },
                  { key: 'protect', icon: Lock, color: 'text-purple-500' }
                ].map(({ key, icon: Icon, color }) => (
                  <Card key={key} className="p-6 border-[hsl(var(--color-border)/0.5)] hover:shadow-lg transition-all duration-300">
                    <div className={`w-12 h-12 rounded-xl bg-[hsl(var(--color-muted))] flex items-center justify-center mb-4 ${color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-[hsl(var(--color-foreground))]">
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </h3>
                    <p className="text-[hsl(var(--color-muted-foreground))]">
                      {t(`whatWeDo.tools.${key}`)}
                    </p>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Free vs Pro Experience */}
          <section className="py-20 bg-[hsl(var(--color-background))] border-y border-[hsl(var(--color-border))]">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Free Experience */}
                <div className="p-8 rounded-3xl bg-[hsl(var(--color-muted)/0.3)] border border-[hsl(var(--color-border))]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-full bg-emerald-500/10">
                      <Heart className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))]">
                      {t('freeExperience.title')}
                    </h2>
                  </div>
                  <p className="text-[hsl(var(--color-muted-foreground))] mb-6">
                    {t('freeExperience.description')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-[hsl(var(--color-foreground))]">{t('freeExperience.dailyTasks')}</p>
                    </div>
                    <div className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-[hsl(var(--color-foreground))]">{t('freeExperience.noPayment')}</p>
                    </div>
                  </div>
                </div>

                {/* Pro Experience */}
                <div className="p-8 rounded-3xl bg-[hsl(var(--color-primary)/0.03)] border border-[hsl(var(--color-primary)/0.1)] relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-3">
                    <span className="px-3 py-1 bg-[hsl(var(--color-primary))] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                      PRO
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-full bg-[hsl(var(--color-primary)/0.1)]">
                      <Zap className="w-6 h-6 text-[hsl(var(--color-primary))]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[hsl(var(--color-foreground))]">
                      {t('proFeatures.title')}
                    </h2>
                  </div>
                  <p className="text-[hsl(var(--color-muted-foreground))] mb-6">
                    {t('proFeatures.description')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <Zap className="w-5 h-5 text-[hsl(var(--color-primary))] shrink-0 mt-0.5" />
                      <p className="text-sm text-[hsl(var(--color-foreground))]">{t('proFeatures.advancedTools')}</p>
                    </div>
                    <div className="flex gap-3">
                      <Zap className="w-5 h-5 text-[hsl(var(--color-primary))] shrink-0 mt-0.5" />
                      <p className="text-sm text-[hsl(var(--color-foreground))]">{t('proFeatures.batchProcessing')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Credit System */}
          <section className="py-20 relative overflow-hidden  rounded-3xl my-8">
            <div className="absolute inset-0 bg-[hsl(var(--color-muted)/0.3)] -skew-y-3 origin-top-left scale-110 z-0" />
            <div className="container relative z-10 mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center mb-16">
                <span className="text-[hsl(var(--color-primary))] font-semibold tracking-wider text-sm uppercase mb-2 block">
                  {t('creditSystem.revolutionaryPaymentModel')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[hsl(var(--color-foreground))]">
                  {t('creditSystem.title')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    title: t('creditSystem.whatAreCredits'),
                    icon: CreditCard,
                    desc: 'Flexible currency, no locked-in subscriptions.'
                  },
                  {
                    title: t('creditSystem.howItWorks'),
                    icon: RefreshCw,
                    desc: 'Earn or buy. Never expires.'
                  },
                  {
                    title: t('creditSystem.advantage'),
                    icon: Award,
                    desc: 'Full control over spending and usage.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-[hsl(var(--color-background))] p-8 rounded-2xl shadow-sm border border-[hsl(var(--color-border))] hover:-translate-y-1 transition-transform">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-blue-500" />
                    </div>
                    <p className="text-[hsl(var(--color-muted-foreground))]">
                      {item.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Privacy & Security */}
          <section className="py-20 bg-[hsl(var(--color-foreground))] text-[hsl(var(--color-background))] rounded-3xl my-8">
            <div className="container mx-auto px-4">
              <div className="flex flex-col lg:flex-row items-center gap-7">
                <div className="lg:w-1/2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    {t('privacySecurity.title')}
                  </h2>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Shield className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{t('privacySecurity.secure')}</h3>
                        <p className="text-[hsl(var(--color-background))/0.7]">{t('privacySecurity.shortDec')}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                        <RefreshCw className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{t('privacySecurity.storage')}</h3>
                        <p className="text-[hsl(var(--color-background))/0.7]">{t('privacySecurity.shortDec1')}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                        <Lock className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">{t('privacySecurity.deletion')}</h3>
                        <p className="text-[hsl(var(--color-background))/0.7]">{t('privacySecurity.shortDec2')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:w-1/2 relative">
                  <div className="aspect-square rounded-full bg-gradient-to-tr from-blue-600 to-emerald-600 opacity-20 blur-3xl absolute inset-0" />
                  {/* Fallback visual if image fails */}
                  <div className="relative z-10 grid grid-cols-2 gap-2 bg-[hsl(var(--color-foreground))]">
                    <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10">
                      <Lock className="w-8 h-8 text-emerald-400 mb-4" />
                      <div className="h-2 w-20 bg-white/20 rounded mb-2"></div>
                      <div className="h-2 w-12 bg-white/20 rounded"></div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/10 mt-8">
                      <Shield className="w-8 h-8 text-blue-400 mb-4" />
                      <div className="h-2 w-20 bg-white/20 rounded mb-2"></div>
                      <div className="h-2 w-12 bg-white/20 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Target Audience */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-center mb-16 text-[hsl(var(--color-foreground))]">
                {t('targetAudience.title')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: 'students', icon: GraduationCap },
                  { label: 'professionals', icon: Briefcase },
                  { label: 'businesses', icon: Building2 },
                  { label: 'teams', icon: Users }
                ].map(({ label, icon: Icon }) => (
                  <div key={label} className="flex flex-col items-center text-center p-6 rounded-2xl bg-[hsl(var(--color-muted))/0.5]">
                    <Icon className="w-8 h-8 text-[hsl(var(--color-primary))] mb-4" />
                    <h3 className="font-semibold text-[hsl(var(--color-foreground))]">
                      {t(`targetAudience.${label}`)}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="py-20 bg-[hsl(var(--color-muted)/0.3)] rounded-3xl my-8">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="grid md:grid-cols-2 gap-12">
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-[hsl(var(--color-primary))]">{t('missionVision.Mission')}</h3>
                    <p className="text-lg text-[hsl(var(--color-foreground))] font-medium leading-relaxed">
                      "{t('missionVision.mission')}"
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-[hsl(var(--color-primary))]">{t('missionVision.Vision')}</h3>
                    <p className="text-lg text-[hsl(var(--color-foreground))] font-medium leading-relaxed">
                      "{t('missionVision.vision')}"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24 text-center">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-4xl font-bold text-[hsl(var(--color-foreground))] mb-8">
                  {t('cta.title')}
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href={`/${locale}/tools`}>
                    <Button size="xl" className="w-full sm:w-auto text-lg px-8 py-6 rounded-2xl shadow-xl shadow-primary/20">
                      {t('cta.freeBtn')}
                    </Button>
                  </Link>
                  <Link href={`/${locale}/pricing`}>
                    <Button variant="outline" size="xl" className="w-full sm:w-auto text-lg px-8 py-6 rounded-2xl">
                      {t('cta.proBtn')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer locale={locale} />
    </div>
  );
}
